import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, PageHeader } from "@/components/kit";
import { Warning, Trash } from "@phosphor-icons/react";

const LABELS = {
  customers: "Customers",
  savings: "Savings accounts",
  savings_tx: "Savings transactions (ledger)",
  loans: "Loans",
  loan_tx: "Loan transactions (repayments/charges)",
  collections: "Collections (receipts)",
  expenses: "Expenses",
  edit_requests: "Customer edit requests",
  notifications: "Notifications",
  loan_requests: "Loan requests",
  pending_collections: "Pending UPI collections",
  branches: "Branches (DANGER — will empty)",
  settings: "Settings (UPI VPA, penalty defaults)",
};

export default function ResetPage() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [picked, setPicked] = useState({});
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (user.role !== "super_admin") return;
    api.get("/admin/reset-info").then(({ data }) => {
      setInfo(data);
      const init = {};
      data.transactional_default.forEach((n) => { init[n] = true; });
      setPicked(init);
    }).catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [user]);

  if (user.role !== "super_admin") {
    return <div className="p-8 text-slate-500">Data reset is restricted to Super Admin.</div>;
  }

  const toggle = (k) => setPicked((p) => ({ ...p, [k]: !p[k] }));
  const selected = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const submit = async (e) => {
    e.preventDefault();
    if (confirmText !== "RESET") {
      setErr('Type RESET exactly to confirm.');
      return;
    }
    if (selected.length === 0) {
      setErr("Select at least one collection to wipe.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/reset", { confirm: "RESET", collections: selected });
      setDone(data.wiped);
      setConfirmText("");
      const { data: info2 } = await api.get("/admin/reset-info");
      setInfo(info2);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="reset-page">
      <PageHeader
        title="Reset Data"
        subtitle="Wipe seed / test data before going live. Users are always preserved."
      />

      <Card className="p-5 mb-4 border-rose-200 bg-rose-50/50">
        <div className="flex items-start gap-3">
          <Warning size={22} weight="fill" className="text-rose-600 shrink-0" />
          <div className="text-sm text-slate-700">
            This action is <b>permanent</b>. Selected collections will be emptied.
            Users (including you) are never touched. Consider taking a database backup before proceeding.
          </div>
        </div>
      </Card>

      {done && (
        <Card className="p-5 mb-4 border-emerald-200 bg-emerald-50/50" data-testid="reset-done">
          <div className="font-semibold text-emerald-800 mb-2">Reset complete</div>
          <ul className="text-sm text-emerald-900 space-y-0.5 mono">
            {Object.entries(done).map(([k, v]) => <li key={k}>{k}: {v} document(s) wiped</li>)}
          </ul>
        </Card>
      )}

      {info && (
        <form onSubmit={submit} className="space-y-4">
          <Card className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3">
              Collections
            </div>
            <div className="space-y-2">
              {info.collections.map((name) => (
                <label key={name} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer border border-slate-100" data-testid={`reset-toggle-${name}`}>
                  <input
                    type="checkbox"
                    checked={!!picked[name]}
                    onChange={() => toggle(name)}
                    className="mt-0.5 accent-emerald-700"
                    data-testid={`reset-cb-${name}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{LABELS[name] || name}</div>
                    <div className="text-[11px] text-slate-500 mono">{name} · {info.counts[name] ?? 0} document(s)</div>
                  </div>
                </label>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <label className="block">
              <div className="text-xs font-medium text-slate-600 mb-1.5">
                Type <span className="mono font-bold text-rose-700">RESET</span> to confirm
              </div>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="reset-confirm-input"
                placeholder="RESET"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mono focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
              />
            </label>
          </Card>

          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">{err}</div>}

          <div className="flex justify-end">
            <Button type="submit" disabled={busy || confirmText !== "RESET" || selected.length === 0}
              className="!bg-rose-600 hover:!bg-rose-700" data-testid="reset-submit">
              <Trash size={16} weight="bold" /> {busy ? "Wiping…" : `Wipe ${selected.length} collection(s)`}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
